"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Edit,
  Trash2,
  X,
  Plus,
  ClipboardCheck,
  ImagePlus,
  Clipboard,
  Eye,
  History,
  ChevronsLeft,
  ChevronsRight,
  Tag,
  FolderKanban,
  FileText,
  AlignLeft,
  MessageSquare,
  ListChecks,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useTaskBugs } from "../../context/TaskBugsContext";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectContext";
import { useTaskContext } from "../../context/TaskContext";
import { CDN_URL } from "../../config/apiConfig";
import TaskModal from "./TaskModal";
import { useTaskProgress } from "../../context/TaskProgressContext";

// ── Constants ────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  bug_id: "",
  project_id: "",
  request_type: "",
  bug_title: "",
  bug_description: "",
  status: "Open",
  comments: "",
  tester_id: "",
  start_date: "",
  end_date: "",
  team: "",
  priority: "",
  estimated_hours: "",
  points: "",
  tags: [],
  links: [],
  images: [],
};

const STATUS_LABEL = {
  Open: "Open",
  Inprogress: "In Progress",
  Qa: "Ready For QA",
  Inreview: "QA In Progress",
  "In Review": "QA In Progress",
  Deployment: "Pending Pre Prod Dep",
  ReadyForProduction: "Ready for Production",
  Fixed: "Done",
  Reopened: "Reopened",
  Closed: "Cancelled",
};

const STATUS_OPTIONS = [
  "Open",
  "Inprogress",
  "Deployment",
  "Qa",
  "Inreview",
  "ReadyForProduction",
  "Fixed",
  "Reopened",
  "Closed",
];

const STATUS_CLASS = {
  Open: "bg-yellow-100 text-yellow-700",
  Inprogress: "bg-orange-100 text-orange-700",
  "In Progress": "bg-orange-100 text-orange-700",
  Qa: "bg-blue-100 text-blue-700",
  "Ready For QA": "bg-blue-100 text-blue-700",
  Inreview: "bg-teal-100 text-teal-700",
  "QA In Progress": "bg-teal-100 text-teal-700",
  "In Review": "bg-teal-100 text-teal-700",
  Deployment: "bg-cyan-100 text-cyan-700",
  "Pending Pre Prod Dep": "bg-cyan-100 text-cyan-700",
  ReadyForProduction: "bg-emerald-100 text-emerald-700",
  "Ready for Production": "bg-emerald-100 text-emerald-700",
  Fixed: "bg-green-100 text-green-700",
  Done: "bg-green-100 text-green-700",
  Reopened: "bg-purple-100 text-purple-700",
  Closed: "bg-red-100 text-red-700",
  Cancelled: "bg-red-100 text-red-700",
};

const STATUS_DOT = {
  Open: "bg-yellow-500",
  Inprogress: "bg-orange-500",
  "In Progress": "bg-orange-500",
  Qa: "bg-blue-500",
  "Ready For QA": "bg-blue-500",
  Inreview: "bg-teal-500",
  "QA In Progress": "bg-teal-500",
  "In Review": "bg-teal-500",
  Deployment: "bg-cyan-500",
  "Pending Pre Prod Dep": "bg-cyan-500",
  ReadyForProduction: "bg-emerald-500",
  "Ready for Production": "bg-emerald-500",
  Fixed: "bg-green-500",
  Done: "bg-green-500",
  Reopened: "bg-purple-500",
  Closed: "bg-red-500",
  Cancelled: "bg-red-500",
};

const COMMENT_LIMIT = 60;

// ── Image URL helper ─────────────────────────────────────────────────────────
// Some records may already store a full URL for an image, others store just a
// relative filename that needs the CDN prefix. Prepending CDN_URL blindly to an
// already-full URL produces a broken, double-prefixed src that fails silently
// in an <img> tag (while the "real" URL still opens fine on its own). This
// normalizes both cases so images actually render.
const getImageUrl = (img) => {
  if (!img) return "";
  if (/^https?:\/\//i.test(img) || img.startsWith("//")) return img;
  const base = CDN_URL?.endsWith("/") ? CDN_URL.slice(0, -1) : CDN_URL;
  const path = img.startsWith("/") ? img : `/${img}`;
  return `${base}${path}`;
};

// ── Status normalization ─────────────────────────────────────────────────────
// Maps display label → internal key (reverse of STATUS_LABEL)
const LABEL_TO_STATUS = Object.fromEntries(
  Object.entries(STATUS_LABEL).map(([k, v]) => [v, k])
);

// Normalizes ANY status value (internal key OR display label) → internal key
const normalizeStatus = (s) => {
  const statusMap = {
    Open: "Open",

    Inprogress: "Inprogress",
    "In Progress": "Inprogress",

    Qa: "Qa",
    "Ready For QA": "Qa",

    Inreview: "Inreview",
    "In Review": "Inreview",
    "QA In Progress": "Inreview",

    Deployment: "Deployment",
    "Pending Pre Prod Dep": "Deployment",

    ReadyForProduction: "ReadyForProduction",
    "Ready for Production": "ReadyForProduction",

    Fixed: "Fixed",
    Done: "Fixed",

    Reopened: "Reopened",

    Closed: "Closed",
    Cancelled: "Closed",
  };

  return statusMap[s] || s;
};

// ── Sub-components ────────────────────────────────────────────────────────────
function CommentCell({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > COMMENT_LIMIT;
  return (
    <span>
      {isLong && !expanded ? text.slice(0, COMMENT_LIMIT) + "…" : text}
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 text-blue-600 text-xs font-semibold underline underline-offset-2 hover:text-blue-800 focus:outline-none whitespace-nowrap"
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </span>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
        {label}
      </p>
      <p className="text-sm text-gray-800">
        {value || value === 0 ? value : "—"}
      </p>
    </div>
  );
}

function ImageThumb({ src, onRemove, label }) {
  return (
    <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-gray-100 group">
      <img src={src} alt={label ?? "preview"} className="w-full h-full object-cover" />
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-red-600 transition"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function ImageUploadZone({
  newPreviews,
  existingImages,
  onAddFiles,
  onRemoveNew,
  onRemoveExisting,
}) {
  const inputRef = useRef(null);
  const zoneRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [pasteFlash, setPasteFlash] = useState(false);

  const handlePaste = useCallback(
    (e) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageFiles = items
        .filter((item) => item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (imageFiles.length > 0) {
        onAddFiles(imageFiles);
        setPasteFlash(true);
        setTimeout(() => setPasteFlash(false), 600);
      }
    },
    [onAddFiles]
  );

  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length) onAddFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) onAddFiles(files);
    e.target.value = "";
  };

  const hasImages = newPreviews.length > 0 || existingImages.length > 0;

  return (
    <div className="space-y-3">
      <label className="text-sm text-gray-700 font-medium">Images</label>
      <div
        ref={zoneRef}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-5 cursor-pointer transition-colors select-none
          ${dragging ? "border-[#8c57ff] bg-[#8c57ff]/5" : "border-gray-300 hover:border-[#8c57ff]/60 hover:bg-gray-50"}
          ${pasteFlash ? "border-green-500 bg-green-50" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileInput}
        />
        <ImagePlus size={22} className="text-gray-400" />
        <p className="text-sm text-gray-500 text-center leading-snug">
          <span className="font-medium text-[#8c57ff]">Click to browse</span>{" "}
          or drag &amp; drop
        </p>
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <Clipboard size={12} />
          Press{" "}
          <kbd className="bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-[10px] font-mono">
            Ctrl+V
          </kbd>{" "}
          to paste a screenshot
        </p>
        {pasteFlash && (
          <span className="absolute top-2 right-3 text-xs text-green-600 font-semibold animate-pulse">
            Pasted!
          </span>
        )}
      </div>
      {hasImages && (
        <div className="flex flex-wrap gap-3">
          {existingImages.map((img, i) => (
            <ImageThumb
              key={`existing-${i}`}
              src={getImageUrl(img)}
              label="existing"
              onRemove={() => onRemoveExisting(i)}
            />
          ))}
          {newPreviews.map((src, i) => (
            <ImageThumb
              key={`new-${i}`}
              src={src}
              label="new"
              onRemove={() => onRemoveNew(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Avatar helpers ────────────────────────────────────────────────────────────
const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0][0]?.toUpperCase();
  return parts[0][0]?.toUpperCase() + parts[parts.length - 1][0]?.toUpperCase();
};

const AVATAR_COLORS = [
  "bg-indigo-500",
  "bg-green-500",
  "bg-blue-500",
  "bg-pink-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-red-500",
  "bg-teal-500",
];

const Avatar = ({ name }) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const bg = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  const fullName = name?.trim() || "User";
  return (
    <div className="relative group inline-flex">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold ${bg}`}
      >
        {getInitials(fullName)}
      </div>
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-[11px] px-2 py-1 rounded z-50 whitespace-normal max-w-[200px] break-words text-center">
        {fullName}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function RequestManagement() {
  const { bugs, fetchAllBugs, createBug, updateBug, deleteBug, loading } =
    useTaskBugs();
  const { canDo } = useAuth();
  const { projects } = useProjects();
  const { tasks, fetchTasks } = useTaskContext();
  const { addTaskProgress } = useTaskProgress();

  const userId = JSON.parse(localStorage.getItem("user"))?.id;

  // ── Modal state ──────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedBug, setSelectedBug] = useState(null);

  // ── Task-conversion modal ────────────────────────────────────
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [prefillTaskData, setPrefillTaskData] = useState(null);

  // ── Progress modal ───────────────────────────────────────────
  const [taskModal, setTaskModal] = useState(false);
  const [progressForm, setProgressForm] = useState({
    bug_id: "",
    task_id: "",
    user_id: userId,
    date: new Date().toISOString().split("T")[0],
    completed_hours: "",
    message: "",
    create_audit_id: userId,
  });

  // ── Form state ───────────────────────────────────────────────
  const [form, setForm] = useState(INITIAL_FORM);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedExistingImages, setRemovedExistingImages] = useState([]);

  const [historyModal, setHistoryModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);

  // ── Full details view modal ───────────────────────────────────
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [selectedViewBug, setSelectedViewBug] = useState(null);

  // ── Image lightbox preview ────────────────────────────────────
  const [previewImage, setPreviewImage] = useState(null);

  // ── Filter state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterProject, setFilterProject] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [viewingTid, setViewingTid] = useState(null);
  const [viewingTaskId, setViewingTaskId] = useState(null);

  // ── Pagination ───────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ── Derived: live prefill for task modal ─────────────────────
  const livePrefillData = React.useMemo(() => {
    if (!viewingTaskId) return prefillTaskData;
    const matchedTask = tasks.find((t) => t.task_id === viewingTaskId);
    if (!matchedTask) return prefillTaskData;
    const bug = bugs.find((b) => b.task_id === viewingTaskId);
    return {
      id: matchedTask.id ?? "",
      task_id: viewingTaskId,
      tid: bug?.tid || matchedTask?.tid || "",
      title: matchedTask.title || bug?.bug_title || "",
      description: matchedTask.description || bug?.bug_description || "",
      status: matchedTask.status || "To Do",
      project_id: matchedTask.project_id || "",
      start_date: matchedTask.start_date || "",
      end_date: matchedTask.end_date || "",
      time: matchedTask.time || "",
      points: matchedTask.points || "",
      priority: matchedTask.priority || "",
      tags: matchedTask.type || [],
      links:
        Array.isArray(matchedTask.links) && matchedTask.links.length > 0
          ? matchedTask.links
          : matchedTask.links && matchedTask.links !== ""
          ? [matchedTask.links]
          : [],
      assignee_name: matchedTask.assignee_name || "",
      assignee_avatar: matchedTask.avatar || "",
      user_id: matchedTask.user_id || "",
      comments: matchedTask?.comments || bug?.comments || "",
    };
  }, [viewingTaskId, tasks, bugs, prefillTaskData]);

  // ── Derived: unique projects in table ────────────────────────
  const projectsInTable = React.useMemo(() => {
    const map = {};
    bugs.forEach((bug) => {
      if (bug.project_id && !map[bug.project_id])
        map[bug.project_id] = { id: bug.project_id, name: bug.project_name };
    });
    return Object.values(map);
  }, [bugs]);

  // ── Effects ──────────────────────────────────────────────────
  useEffect(() => {
    fetchAllBugs();
  }, []);
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus, filterProject, filterType, fromDate, toDate]);

  // ── Base filtered rows (search + project + type + date) ────────
  const q = searchTerm.toLowerCase();
  const baseRows = React.useMemo(
    () =>
      bugs.filter((bug) => {
        const activityDate = (
          bug.update_audit_time || bug.create_audit_time
        )?.split(" ")[0];
        return (
          (filterProject === "All" || bug.project_id == filterProject) &&
          (filterType === "All" ||
            (filterType === "CR" ? bug.type === "CR" : bug.type !== "CR")) &&
          (!fromDate || activityDate >= fromDate) &&
          (!toDate || activityDate <= toDate) &&
          (String(bug.id).toLowerCase().includes(q) ||
            (bug.type === "CR"
              ? `cr-${bug.project_code}`.toLowerCase().includes(q)
              : `bug-${bug.project_code}`.toLowerCase().includes(q)) ||
            bug.tester_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bug.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bug.project_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bug.bug_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bug.bug_description
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            bug.task_id?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }),
    [bugs, searchTerm, filterProject, filterType, fromDate, toDate]
  );

  // ── Status counts — normalized so display labels also match ──
  const statusCounts = React.useMemo(() => {
    const counts = {};
    STATUS_OPTIONS.forEach((s) => {
      counts[s] = 0;
    });
    baseRows.forEach((bug) => {
      const key = normalizeStatus(bug.status);
      if (counts[key] !== undefined) counts[key]++;
    });
    return counts;
  }, [baseRows]);

  // ── Status-filtered rows — normalized comparison ──────────────
  const filteredRows = React.useMemo(() => {
    return baseRows.filter(
      (bug) =>
        filterStatus.length === 0 ||
        filterStatus.includes(normalizeStatus(bug.status))
    );
  }, [baseRows, filterStatus]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [filteredRows.length, rowsPerPage]);

  const currentRows = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // ── Image helpers ─────────────────────────────────────────────
  const addImageFiles = useCallback((files) => {
    setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...files] }));
    setImagePreviews((prev) => [
      ...prev,
      ...files.map((f) => URL.createObjectURL(f)),
    ]);
  }, []);

  const removeNewImage = (index) => {
    setForm((prev) => {
      const images = [...(prev.images || [])];
      images.splice(index, 1);
      return { ...prev, images };
    });
    setImagePreviews((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index]);
      updated.splice(index, 1);
      return updated;
    });
  };

  const removeExistingImage = (index) => {
    setRemovedExistingImages((prev) => [...prev, existingImages[index]]);
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Form helpers ──────────────────────────────────────────────
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const resetFormState = () => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setForm(INITIAL_FORM);
    setImagePreviews([]);
    setExistingImages([]);
    setRemovedExistingImages([]);
  };

  const closeModal = () => {
    resetFormState();
    setShowModal(false);
  };

  // ── Submit ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const fd = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key !== "images") fd.append(key, val ?? "");
    });
    fd.append("tester_id", userId);
    fd.append(
      modalType === "create" ? "create_audit_id" : "update_audit_id",
      userId
    );
    (form.images || []).forEach((file) => fd.append("images[]", file));

    if (modalType === "edit") {
      existingImages.forEach((img) =>
        fd.append("existingImagesArray[]", img)
      );
      removedExistingImages.forEach((img) =>
        fd.append("removedImages[]", img)
      );
    }

    const res =
      modalType === "create"
        ? await createBug(fd)
        : await updateBug(selectedBug.id, fd);

    if (res.success) {
      await fetchAllBugs();
      closeModal();
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async () => {
    const res = await deleteBug(selectedBug.id, userId);
    if (res.success) closeModal();
  };

  // ── Open modals ───────────────────────────────────────────────
  const openCreateModal = () => {
    resetFormState();
    setModalType("create");
    setShowModal(true);
  };

  const openEditModal = (bug) => {
    setModalType("edit");
    setSelectedBug(bug);
    // Normalize status to internal key so radio buttons pre-select correctly
    const normalizedStatus = normalizeStatus(bug.status);
    setForm({
      bug_id: bug.bug_id || "",
      project_id: bug.project_id || "",
      request_type: bug.type || "",
      task_id: bug.task_id || "",
      bug_title: bug.bug_title || "",
      bug_description: bug.bug_description || "",
      status: normalizedStatus || "Open",
      comments: bug.comments || "",
      tester_id: bug.tester_id || "",
      start_date: bug.start_date || "",
      end_date: bug.end_date || "",
      team: bug.team || "",
      priority: bug.priority || "",
      estimated_hours: bug.estimated_hours || bug.time || "",
      points: bug.points || "",
      tags: bug.tags || [],
      links: bug.links || [],
      images: [],
    });
    try {
      setExistingImages(
        Array.isArray(bug.images) ? bug.images : JSON.parse(bug.images || "[]")
      );
    } catch {
      setExistingImages([]);
    }
    setShowModal(true);
  };

  const openDeleteModal = (bug) => {
    setModalType("delete");
    setSelectedBug(bug);
    setShowModal(true);
  };

  // ── Progress save ─────────────────────────────────────────────
  const handleSaveProgress = async () => {
    if (!progressForm.task_id) return alert("Task ID missing");
    const res = await addTaskProgress(progressForm);
    if (res.status === 200) {
      alert("Progress added successfully");
      setTaskModal(false);
      fetchTasks();
    } else {
      alert(res.message);
    }
  };

  // ── Pagination buttons ────────────────────────────────────────
  const paginationButtons = () => {
    const start = Math.max(page - 1, 1);
    const end = Math.min(start + 2, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(
      (n) => (
        <button
          key={n}
          onClick={() => setPage(n)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            n === page
              ? "text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
          style={n === page ? { background: "linear-gradient(135deg, #2563eb, #d12465)" } : undefined}
        >
          {n}
        </button>
      )
    );
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-sky-50 min-h-screen">
        <div className="px-6 py-6 sm:px-9 max-sm:px-4">
          <div className="mb-5">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Request Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Track, raise and manage bug reports and change requests across projects.
            </p>
          </div>

          {/* ── Filters ── */}
          <div className="flex flex-wrap items-center gap-3 mb-5 bg-white rounded-2xl border border-gray-100 px-4 py-4 shadow-md">
            <input
              type="text"
              placeholder="Search by requested by, project, title, task conversion or comments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none flex-1 min-w-[220px] focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
            />
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none shrink-0 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
            >
              <option value="All">All Projects</option>
              {projectsInTable.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none shrink-0 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
            >
              <option value="All">All Types</option>
              <option value="CR">Change Request</option>
              <option value="BUG">Bug</option>
            </select>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none shrink-0 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none shrink-0 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
            />

            {(searchTerm || filterStatus.length > 0 || filterProject !== "All" || filterType !== "All" || fromDate || toDate) && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus([]);
                  setFilterProject("All");
                  setFilterType("All");
                  setFromDate("");
                  setToDate("");
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}

            {canDo("RequestManagement", "Add") && (
              <button
                onClick={openCreateModal}
                className="flex items-center justify-center gap-1.5 text-white px-6 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap hover:opacity-90 shrink-0 ml-auto"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                <Plus size={16} />
                Create Request
              </button>
            )}
          </div>

          {/* ── Status summary cards (clickable filter) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
            {STATUS_OPTIONS.map((status) => {
              const active = filterStatus.includes(status);
              return (
                <div
                  key={status}
                  onClick={() =>
                    setFilterStatus((prev) =>
                      prev.includes(status)
                        ? prev.filter((s) => s !== status)
                        : [...prev, status]
                    )
                  }
                  className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col items-center justify-center gap-1.5
                    ${active ? "border-transparent ring-2 ring-[#8c57ff]" : "border-gray-100"}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status] || "bg-gray-400"}`} aria-hidden="true" />
                    <div className="text-xs font-semibold text-gray-600 text-center leading-tight uppercase tracking-wide">
                      {STATUS_LABEL[status] || status}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-800">
                    {statusCounts[status] || 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Table ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-sky-50">
                    {[
                      "ID",
                      "Requested By",
                      "Project",
                      "Title",
                      "Description",
                      "Task Conversion",
                      "Status",
                    ].map((h) => (
                      <th
                        key={h}
                        className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                    {(canDo("RequestManagement", "Edit") || canDo("RequestManagement", "Delete")) && (
                      <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Actions
                      </th>
                    )}
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? (
                    currentRows.map((bug, index) => {

                      const normalizedKey = normalizeStatus(bug.status);
                      return (
                        <tr
                          key={bug.id}
                          className="border-b border-gray-50 hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="py-3 px-4 text-indigo-600">
                            {bug.type === "CR"
                              ? `CR-${bug.project_code}`
                              : `BUG-${bug.project_code}`}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {bug.tester_name}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {bug.project_name}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {bug.bug_title}
                          </td>
                          <td className="py-3 px-4 text-gray-600 max-w-[220px]">
                            <div className="max-w-[220px] whitespace-normal break-words [overflow-wrap:anywhere]">
                              <CommentCell text={bug.bug_description} />
                            </div>
                          </td>

                          {/* Task Conversion */}
                          <td className="py-3 px-4 text-center">
                            {bug.task_id ? (
                              <div className="relative group cursor-pointer inline-block">
                                <span
                                  className="hover:text-indigo-600 cursor-pointer"
                                  onClick={() => {
                                    setViewingTaskId(bug.task_id);
                                    setViewingTid(bug.tid);
                                    setPrefillTaskData(null);
                                    setViewOnlyMode(true);
                                    setIsTaskModalOpen(true);
                                  }}
                                >
                                  {bug.task_id}
                                </span>
                                <div className="absolute bottom-full mb-2 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  Assigned By: {bug.employee_name} <br />
                                  Task Status: {bug.task_status}
                                </div>
                              </div>
                            ) : (
                              <ClipboardCheck
                                size={16}
                                className="cursor-pointer hover:text-indigo-600 mx-auto"
                                onClick={() => {
                                  setPrefillTaskData(bug);
                                  setIsTaskModalOpen(true);
                                }}
                              />
                            )}
                          </td>

                          {/* Status badge — normalized for display & class lookup */}
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${
                                STATUS_CLASS[normalizedKey] ||
                                STATUS_CLASS[bug.status] ||
                                "bg-gray-100 text-gray-700"
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[normalizedKey] || STATUS_DOT[bug.status] || "bg-gray-400"}`} aria-hidden="true" />
                              {STATUS_LABEL[bug.status] || STATUS_LABEL[normalizedKey] || bug.status}
                            </span>
                          </td>

                          {/* Actions */}
                          {(canDo("RequestManagement", "Edit") ||
                            canDo("RequestManagement", "Delete")) && (
                            <td className="py-3 px-4 text-center">
                              <div className="flex justify-center gap-3 text-gray-600">
                                <Eye
                                  size={16}
                                  className="cursor-pointer hover:text-indigo-600"
                                  title="View Full Details"
                                  onClick={() => {
                                    setSelectedViewBug(bug);
                                    setViewDetailsModal(true);
                                  }}
                                />
                                <History
                                  size={16}
                                  className="cursor-pointer hover:text-indigo-600"
                                  title="View Status History"
                                  onClick={() => {
                                    setSelectedHistory(bug.status_history);
                                    setHistoryModal(true);
                                  }}
                                />
                                {canDo("RequestManagement", "Edit") && (
                                  <Edit
                                    size={16}
                                    className="cursor-pointer hover:text-indigo-600"
                                    onClick={() => openEditModal(bug)}
                                  />
                                )}
                                {canDo("RequestManagement", "Delete") && (
                                  <Trash2
                                    size={16}
                                    className="cursor-pointer hover:text-red-600"
                                    onClick={() => openDeleteModal(bug)}
                                  />
                                )}
                              </div>
                            </td>
                          )}

                          <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                            {new Date(
                              bug.update_audit_time || bug.create_audit_time
                            ).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-10 text-gray-400 text-sm">
                        No requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 15].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <MdKeyboardArrowLeft size={16} />
                </button>
                {paginationButtons()}
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <MdKeyboardArrowRight size={16} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Progress Modal ── */}
      {taskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl border border-gray-100 relative max-sm:max-w-sm max-h-[60vh] overflow-y-auto space-y-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
            <h2 className="text-lg font-bold text-gray-800">Add Task Progress</h2>
            <div>
              <label className="text-xs font-semibold text-gray-600">Date</label>
              <input
                type="date"
                value={progressForm.date}
                onChange={(e) =>
                  setProgressForm((p) => ({ ...p, date: e.target.value }))
                }
                className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Completed Hours</label>
              <input
                type="number"
                value={progressForm.completed_hours}
                onChange={(e) =>
                  setProgressForm((p) => ({
                    ...p,
                    completed_hours: e.target.value,
                  }))
                }
                className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Message</label>
              <textarea
                rows={3}
                value={progressForm.message}
                onChange={(e) =>
                  setProgressForm((p) => ({ ...p, message: e.target.value }))
                }
                className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setTaskModal(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProgress}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white shadow-sm hover:opacity-90 transition"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Task Conversion Modal ── */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setPrefillTaskData(null);
          setViewingTaskId(null);
          setViewOnlyMode(false);
        }}
        prefillData={livePrefillData}
        viewOnly={viewOnlyMode}
        onSaved={fetchAllBugs}
      />

      {/* ── Status History Modal ── */}
      {historyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-[520px] max-h-[80vh] overflow-y-auto p-5 relative shadow-xl border border-gray-100">
            <button
              onClick={() => setHistoryModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <X size={18} />
            </button>
            <h2 className="text-lg font-bold text-gray-800 mb-4">Status Timeline</h2>
            {selectedHistory && selectedHistory.length > 0 ? (
              <div className="space-y-4">
                {selectedHistory.map((item, index) => (
                  <div key={index} className="relative pl-5">
                    <div className="absolute left-0 top-2 w-2 h-2 bg-blue-600 rounded-full" />
                    {index !== selectedHistory.length - 1 && (
                      <div className="absolute left-[3px] top-4 w-[2px] h-full bg-gray-200" />
                    )}
                    <div className="border border-gray-100 rounded-xl p-3 bg-gray-50 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-800 font-medium">
                          {item.from_status || "Start"} → {item.to_status}
                        </div>
                        <Avatar
                          name={
                            item.user_name ||
                            `${item.first_name || ""} ${item.last_name || ""}`
                          }
                        />
                      </div>
                      {item.remarks && (
                        <div className="text-xs text-gray-600 mt-2">
                          {item.remarks}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400 mt-2">
                        {new Date(item.created_at).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No history found</p>
            )}
          </div>
        </div>
      )}

      {/* ── Full Details View Modal ── */}
      {viewDetailsModal && selectedViewBug && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 relative shadow-xl border border-gray-100 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            <button
              onClick={() => {
                setViewDetailsModal(false);
                setSelectedViewBug(null);
                setPreviewImage(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                <Eye size={18} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedViewBug.type === "CR"
                    ? `CR-${selectedViewBug.project_code}`
                    : `BUG-${selectedViewBug.project_code}`}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedViewBug.bug_title}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Detail label="Project" value={selectedViewBug.project_name} />
              <Detail label="Requested By" value={selectedViewBug.tester_name} />
              <Detail
                label="Type"
                value={selectedViewBug.type === "CR" ? "Change Request" : "Bug"}
              />
              <Detail
                label="Status"
                value={
                  STATUS_LABEL[normalizeStatus(selectedViewBug.status)] ||
                  selectedViewBug.status
                }
              />
              <Detail label="Team" value={selectedViewBug.team} />
              <Detail label="Priority" value={selectedViewBug.priority} />
              <Detail label="Start Date" value={selectedViewBug.start_date} />
              <Detail label="End Date" value={selectedViewBug.end_date} />
              <Detail
                label="Estimated Hours"
                value={selectedViewBug.estimated_hours || selectedViewBug.time}
              />
              <Detail label="Points" value={selectedViewBug.points} />
              <Detail
                label="Task Conversion"
                value={selectedViewBug.task_id || "Not converted"}
              />
              {selectedViewBug.task_id && (
                <>
                  <Detail label="Assigned By" value={selectedViewBug.employee_name} />
                  <Detail label="Task Status" value={selectedViewBug.task_status} />
                </>
              )}
              <Detail
                label="Last Updated"
                value={new Date(
                  selectedViewBug.update_audit_time || selectedViewBug.create_audit_time
                ).toLocaleString("en-IN")}
              />
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Description
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {selectedViewBug.bug_description || "—"}
              </p>
            </div>

            {selectedViewBug.comments && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Comments
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedViewBug.comments}
                </p>
              </div>
            )}

            {Array.isArray(selectedViewBug.tags) && selectedViewBug.tags.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Tags
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedViewBug.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(selectedViewBug.links) && selectedViewBug.links.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Links
                </p>
                <div className="flex flex-col gap-1">
                  {selectedViewBug.links.map((link, i) => (
                    <a
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 text-sm underline break-all"
                    >
                      {link}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              let imgs = [];
              try {
                imgs = Array.isArray(selectedViewBug.images)
                  ? selectedViewBug.images
                  : JSON.parse(selectedViewBug.images || "[]");
              } catch {
                imgs = [];
              }
              if (!imgs.length) return null;
              return (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Images
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {imgs.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPreviewImage(getImageUrl(img))}
                        className="w-24 h-24 border rounded-lg overflow-hidden bg-gray-100 block cursor-zoom-in hover:opacity-90 transition"
                      >
                        <img
                          src={getImageUrl(img)}
                          alt={`attachment-${i}`}
                          className="w-full h-full object-cover pointer-events-none"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Image Lightbox Preview ── */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-[60] p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition"
          >
            <X size={22} />
          </button>
          <img
            src={previewImage}
            alt="preview"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}

      {/* ── Create / Edit / Delete Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className={`bg-white rounded-2xl w-full shadow-2xl relative max-h-[88vh] flex flex-col overflow-hidden ${modalType === "delete" ? "max-w-lg max-sm:max-w-sm p-6" : "max-w-2xl max-sm:max-w-sm"}`}>
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
            >
              <X size={20} />
            </button>

            {modalType === "delete" ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  Delete Request
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{selectedBug?.bug_title}</span>?
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col min-h-0 max-h-[88vh]">
                {/* Sticky header */}
                <div className="flex items-center gap-3 px-6 sm:px-7 pt-6 sm:pt-7 pb-4 border-b border-gray-100 shrink-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                  >
                    {modalType === "create" ? <Plus size={20} /> : <Edit size={18} />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {modalType === "create" ? "Add New Request" : "Edit Request"}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {modalType === "create"
                        ? "Raise a new bug report or change request for a project"
                        : "Update the details of this request"}
                    </p>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="px-6 sm:px-7 py-5 space-y-5 overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Request Type */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <Tag size={14} className="text-gray-400" />
                        Request Type
                      </label>
                      <select
                        name="request_type"
                        value={form.request_type}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
                      >
                        <option value="">Select Request Type</option>
                        <option value="CR">New Request</option>
                        <option value="BUG">Bug</option>
                      </select>
                    </div>

                    {/* Project */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <FolderKanban size={14} className="text-gray-400" />
                        Project
                      </label>
                      <select
                        name="project_id"
                        value={form.project_id}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
                      >
                        <option value="">Select a Project</option>
                        {projects
                          .filter((p) => p.status !== "Cancelled")
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.project_name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <FileText size={14} className="text-gray-400" />
                      Title
                    </label>
                    <input
                      type="text"
                      name="bug_title"
                      value={form.bug_title}
                      onChange={handleChange}
                      required
                      placeholder="Short, descriptive title"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <AlignLeft size={14} className="text-gray-400" />
                      Description
                    </label>
                    <textarea
                      name="bug_description"
                      rows="3"
                      value={form.bug_description}
                      onChange={handleChange}
                      required
                      placeholder="Describe the issue or request in detail"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
                    />
                  </div>

                  {/* Comments */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-gray-400" />
                      Comments <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="comments"
                      rows="2"
                      value={form.comments}
                      onChange={handleChange}
                      placeholder="Additional notes or context"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
                    />
                  </div>

                  {/* Status pills — uses internal keys, shows display labels */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                      <ListChecks size={14} className="text-gray-400" />
                      Status
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      {STATUS_OPTIONS.map((s) => {
                        const label = STATUS_LABEL[s] || s;
                        const active = form.status === s;
                        const activeClass = STATUS_CLASS[label] || STATUS_CLASS[s] || "bg-indigo-100 text-indigo-700";
                        return (
                          <label
                            key={s}
                            className={`cursor-pointer select-none px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                              active
                                ? `${activeClass} border-transparent ring-2 ring-offset-1 ring-[#8c57ff]/60`
                                : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                            }`}
                          >
                            <input
                              type="radio"
                              name="status"
                              value={s}
                              checked={active}
                              onChange={handleChange}
                              className="hidden"
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Image Upload */}
                  <ImageUploadZone
                    newPreviews={imagePreviews}
                    existingImages={modalType === "edit" ? existingImages : []}
                    onAddFiles={addImageFiles}
                    onRemoveNew={removeNewImage}
                    onRemoveExisting={removeExistingImage}
                  />
                </div>

                {/* Sticky footer */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 px-6 sm:px-7 py-4 border-t border-gray-100 shrink-0">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 w-full sm:w-auto transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading
                        ? "#818cf8"
                        : "linear-gradient(135deg, #2563eb, #d12465)",
                    }}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium w-full sm:w-auto flex items-center justify-center gap-2 text-white shadow-sm ${
                      loading ? "cursor-not-allowed" : "hover:opacity-90"
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        {modalType === "create" ? "Creating..." : "Saving..."}
                      </>
                    ) : modalType === "create" ? (
                      <>
                        <Plus size={16} />
                        Create Request
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}